import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import NowPlaying from '../viewer/NowPlaying';
import ArchiveCalendar from '../viewer/ArchiveCalendar';
import StreamingServiceIcon from '../streaming/StreamingServiceIcon';
import { localDateKey, todayKey } from '../../utils/dateKeys';

// Hoisted so the default is one stable function rather than a fresh one per
// render. Both of these are effect dependencies below, and an inline default
// re-creates them every time - which spins the effect, sets state, re-renders,
// and loops forever. App always passes them, so this never fired in anger, but
// it made the component impossible to render without them.
const NO_ARCHIVE_YEARS = async () => [];
const NO_ARCHIVED_WEEKS = async () => [];

const ViewerTab = ({
  calendarWeeks,
  scheduledMovies,
  timeSlots,
  timezone,
  getScheduledMovieForSlot,
  isSlotOccupied,
  getWeekRange,
  onMovieClick,
  archivedWeeks = [],
  getAvailableArchiveYears = NO_ARCHIVE_YEARS,
  getArchivedWeeksForYear = NO_ARCHIVED_WEEKS,
  getScheduledMovieForArchivedSlot,
  isArchivedSlotOccupied,
  isAdmin = false,
  onUnarchiveWeek,
  allMovies = [],
  movieReviews = {},
  getStreamingServiceById,
  movieStatuses = {},
  getMovieStatus,
  loadCalendarData,
  calendarDataLoading = false,
  // The calendar is only fetched for signed-in people, so an empty calendar
  // means two completely different things depending on this.
  isUserAuthenticated = false
}) => {
  const [viewerMode, setViewerMode] = useState('calendar');
  const [currentMovieIndex, setCurrentMovieIndex] = useState(0);
  const [viewerTimezone, setViewerTimezone] = useState('ET');
  const [showPastCalendars, setShowPastCalendars] = useState(false);
  const [selectedArchiveYear, setSelectedArchiveYear] = useState('');
  
  // Lazy load calendar data when ViewerTab is accessed
  useEffect(() => {
    if (loadCalendarData) {
      console.log('📅 ViewerTab mounted - triggering calendar data load');
      loadCalendarData();
    }
  }, [loadCalendarData]);
  const [availableYears, setAvailableYears] = useState([]);
  const [archivedWeeksForYear, setArchivedWeeksForYear] = useState([]);
  const [archivedMoviesCache, setArchivedMoviesCache] = useState({});
  
  // Mobile calendar navigation state
  const [selectedWeekIndex, setSelectedWeekIndex] = useState(0);
  const [selectedDayIndex, setSelectedDayIndex] = useState(0);

  const timezones = [
    { value: 'ET', label: 'Eastern Time (ET)' },
    { value: 'CT', label: 'Central Time (CT)' },
    { value: 'MT', label: 'Mountain Time (MT)' },
    { value: 'PT', label: 'Pacific Time (PT)' }
  ];

  // Load available archive years when component mounts
  useEffect(() => {
    const loadArchiveYears = async () => {
      try {
        const years = await getAvailableArchiveYears();
        setAvailableYears(years);
      } catch (error) {
        console.error('❌ Error loading archive years:', error);
        setAvailableYears([]);
      }
    };

    loadArchiveYears();
  }, [getAvailableArchiveYears]);

  // Load archived weeks when year is selected
  useEffect(() => {
    const loadArchivedWeeks = async () => {
      if (selectedArchiveYear) {
        try {
          const weeks = await getArchivedWeeksForYear(selectedArchiveYear);
          setArchivedWeeksForYear(weeks);
          
          // Also load archived movies for this year
          const { loadScheduledMovies } = await import('../../services/calendarService');
          console.log('🔍 ViewerTab: Loading archived movies for year', selectedArchiveYear);
          const archivedMovies = await loadScheduledMovies(true); // Include archived
          console.log('📊 ViewerTab: Archived movies loaded:', Object.keys(archivedMovies).length, 'dates');
          console.log('🎬 ViewerTab: Archived movies data:', archivedMovies);
          setArchivedMoviesCache(archivedMovies);
        } catch (error) {
          console.error('❌ Error loading archived weeks:', error);
          setArchivedWeeksForYear([]);
          setArchivedMoviesCache({});
        }
      } else {
        setArchivedWeeksForYear([]);
        setArchivedMoviesCache({});
      }
    };

    loadArchivedWeeks();
  }, [selectedArchiveYear, getArchivedWeeksForYear]);

  // Listen for unarchive events to refresh the archived weeks list
  useEffect(() => {
    const handleWeekUnarchived = (event) => {
      const { year } = event.detail;
      // If the unarchived week is from the currently selected year, refresh the list
      if (year && year.toString() === selectedArchiveYear) {
        // Trigger a refresh by re-running the loadArchivedWeeks effect
        const loadArchivedWeeks = async () => {
          try {
            const weeks = await getArchivedWeeksForYear(selectedArchiveYear);
            setArchivedWeeksForYear(weeks);
            
            // Also refresh archived movies cache
            const { loadScheduledMovies } = await import('../../services/calendarService');
            const archivedMovies = await loadScheduledMovies(true);
            setArchivedMoviesCache(archivedMovies);
          } catch (error) {
            console.error('❌ Error refreshing archived weeks after unarchive:', error);
          }
        };
        loadArchivedWeeks();
      }
    };

    window.addEventListener('weekUnarchived', handleWeekUnarchived);
    return () => window.removeEventListener('weekUnarchived', handleWeekUnarchived);
  }, [selectedArchiveYear, getArchivedWeeksForYear]);

  // Which weeks these ARE, rather than which array object delivered them. The
  // parent filters for non-admins, so the prop arrives as a fresh array on
  // every render; depending on it directly re-homed the calendar on today
  // after any state change, closing a movie's modal included.
  const weeksSignature = (calendarWeeks || [])
    .map((week) => `${week[0]?.toISOString?.() ?? ''}`)
    .join('|');

  // Set initial navigation to current date when calendar weeks are available.
  // "Today" is the LOCAL date. It used to be read off toISOString(), which
  // is the UTC date: from 8pm Eastern that is already tomorrow, and the phone
  // calendar opened on the next day every evening, past that night's film.
  useEffect(() => {
    if (calendarWeeks && calendarWeeks.length > 0) {
      const todayDateString = todayKey();

      // Find the week and day that contains today's date
      let foundWeekIndex = -1;
      let foundDayIndex = -1;

      for (let weekIndex = 0; weekIndex < calendarWeeks.length; weekIndex++) {
        const week = calendarWeeks[weekIndex];
        for (let dayIndex = 0; dayIndex < week.length; dayIndex++) {
          const day = week[dayIndex];
          const dayDateString = localDateKey(day);
          
          if (dayDateString === todayDateString) {
            foundWeekIndex = weekIndex;
            foundDayIndex = dayIndex;
            break;
          }
        }
        if (foundWeekIndex !== -1) break;
      }
      
      // If we found today's date, set the navigation to show it
      if (foundWeekIndex !== -1 && foundDayIndex !== -1) {
        console.log(`📅 Found today's date in calendar - Week ${foundWeekIndex + 1}, Day ${foundDayIndex + 1}`);
        setSelectedWeekIndex(foundWeekIndex);
        setSelectedDayIndex(foundDayIndex);
      } else {
        console.log('📅 Today\'s date not found in calendar weeks, using default (first week/day)');
        // Default to first week/day if today is not in the calendar
        setSelectedWeekIndex(0);
        setSelectedDayIndex(0);
      }
    }
    // Keyed on the signature, so this runs when the weeks themselves change and
    // not merely when a new array carrying the same weeks arrives.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weeksSignature]);

  // Get all scheduled movies for now playing view - only from visible calendar weeks
  const getAllScheduledMovies = () => {
    const allScheduled = [];
    
    // Create a set of date keys from visible calendar weeks for fast lookup
    const visibleDateKeys = new Set();
    calendarWeeks.forEach(week => {
      week.forEach(date => {
        const dateKey = date.toISOString().split('T')[0];
        visibleDateKeys.add(dateKey);
      });
    });
    
    // Only include movies from visible weeks
    Object.entries(scheduledMovies).forEach(([dateKey, dayMovies]) => {
      if (visibleDateKeys.has(dateKey)) {
        const date = new Date(dateKey + 'T00:00:00');
        dayMovies.forEach(scheduled => {
          allScheduled.push({
            ...scheduled,
            date: date,
            dateKey: dateKey
          });
        });
      }
    });
    
    return allScheduled;
  };

  // Get upcoming movies based on status (playing, ready, coming, soon)
  // eslint-disable-next-line no-unused-vars -- dead code, kept for now
  const getUpcomingMovies = () => {
    // Filter movies by status
    const statusMovies = allMovies.filter(movie => {
      const status = getMovieStatus ? getMovieStatus(movie.id) : 'coming';
      const validStatuses = ['playing', 'ready', 'coming', 'soon'];
      return validStatuses.includes(status);
    });
    
    // Sort by status priority, then by title
    const statusPriority = { 'playing': 1, 'ready': 2, 'coming': 3, 'soon': 4 };
    
    return statusMovies.sort((a, b) => {
      const statusA = getMovieStatus ? getMovieStatus(a.id) : 'coming';
      const statusB = getMovieStatus ? getMovieStatus(b.id) : 'coming';
      
      const priorityA = statusPriority[statusA] || 5;
      const priorityB = statusPriority[statusB] || 5;
      
      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }
      
      // If same status, sort by title
      return a.title.localeCompare(b.title);
    });
  };

  const upcomingMovies = getAllScheduledMovies();
  
  // Ensure currentMovieIndex is within bounds
  const validIndex = Math.max(0, Math.min(currentMovieIndex, upcomingMovies.length - 1));
  const currentMovie = upcomingMovies[validIndex];

  // Convert time to viewer's timezone
  const convertTimeToViewerTimezone = (time, fromTimezone = timezone) => {
    // Add comprehensive validation
    if (!time || typeof time !== 'string') {
      console.warn('⚠️ Invalid time passed to convertTimeToViewerTimezone:', time);
      return time || '';
    }
    
    if (fromTimezone === viewerTimezone) return time;
    
    // This is a simplified conversion - in a real app you'd use a proper timezone library
    const timezoneOffsets = { ET: -5, CT: -6, MT: -7, PT: -8 };
    const fromOffset = timezoneOffsets[fromTimezone] || -5;
    const toOffset = timezoneOffsets[viewerTimezone] || -5;
    const hoursDiff = toOffset - fromOffset;
    
    try {
      let hours, minutes = 0;
      
      // Handle different time formats
      if (time.includes(':')) {
        // Format: "14:30" or "2:30 PM"
        const [hourStr, minuteStr] = time.split(':');
        hours = parseInt(hourStr);
        
        // Handle AM/PM in minute part
        if (minuteStr.includes('AM') || minuteStr.includes('PM')) {
          minutes = parseInt(minuteStr.replace(/[^\d]/g, ''));
          if (minuteStr.includes('PM') && hours !== 12) {
            hours += 12;
          } else if (minuteStr.includes('AM') && hours === 12) {
            hours = 0;
          }
        } else {
          minutes = parseInt(minuteStr);
        }
      } else if (time.includes('AM') || time.includes('PM')) {
        // Format: "2 AM" or "2 PM"
        const isPM = time.includes('PM');
        hours = parseInt(time.replace(/[^\d]/g, ''));
        minutes = 0;
        
        if (isPM && hours !== 12) {
          hours += 12;
        } else if (!isPM && hours === 12) {
          hours = 0;
        }
      } else {
        // Format: "14" (24-hour format)
        hours = parseInt(time);
        minutes = 0;
      }
      
      // Validate parsed numbers
      if (isNaN(hours) || isNaN(minutes)) {
        console.warn('⚠️ Invalid time format in convertTimeToViewerTimezone:', time);
        return time;
      }
      
      // Apply timezone conversion
      let newHours = hours + hoursDiff;
      
      // Handle day overflow/underflow
      if (newHours < 0) newHours += 24;
      if (newHours >= 24) newHours -= 24;
      
      // Convert back to 12-hour format if original was AM/PM
      if (time.includes('AM') || time.includes('PM')) {
        const period = newHours >= 12 ? 'PM' : 'AM';
        const displayHours = newHours === 0 ? 12 : newHours > 12 ? newHours - 12 : newHours;
        return minutes > 0 ? `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}` : `${displayHours} ${period}`;
      } else {
        // Return in 24-hour format
        return `${newHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
      }
    } catch (error) {
      console.error('❌ Error in convertTimeToViewerTimezone:', error, 'time:', time);
      return time;
    }
  };

  // Mobile navigation helpers
  const getCurrentWeek = () => calendarWeeks[selectedWeekIndex] || [];
  const getCurrentDay = () => getCurrentWeek()[selectedDayIndex];
  
  const navigateDay = (direction) => {
    const currentWeek = getCurrentWeek();
    if (direction === 'next') {
      if (selectedDayIndex < currentWeek.length - 1) {
        setSelectedDayIndex(selectedDayIndex + 1);
      } else if (selectedWeekIndex < calendarWeeks.length - 1) {
        setSelectedWeekIndex(selectedWeekIndex + 1);
        setSelectedDayIndex(0);
      }
    } else if (direction === 'prev') {
      if (selectedDayIndex > 0) {
        setSelectedDayIndex(selectedDayIndex - 1);
      } else if (selectedWeekIndex > 0) {
        setSelectedWeekIndex(selectedWeekIndex - 1);
        setSelectedDayIndex(calendarWeeks[selectedWeekIndex - 1].length - 1);
      }
    }
  };

  const canNavigatePrev = () => {
    return selectedWeekIndex > 0 || selectedDayIndex > 0;
  };

  const canNavigateNext = () => {
    const currentWeek = getCurrentWeek();
    return selectedWeekIndex < calendarWeeks.length - 1 || selectedDayIndex < currentWeek.length - 1;
  };

  // Synchronous functions to access cached archived movies
  const getArchivedMovieForSlot = (date, timeSlot) => {
    // For archived calendars, shift the lookup one day forward to compensate for timezone shift
    const shiftedDate = new Date(date);
    shiftedDate.setDate(date.getDate() + 1);
    
    // Use timezone-agnostic date string conversion to avoid UTC shift
    const year = shiftedDate.getFullYear();
    const month = String(shiftedDate.getMonth() + 1).padStart(2, '0');
    const day = String(shiftedDate.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    const dayMovies = archivedMoviesCache[dateStr] || [];
    
    for (const movie of dayMovies) {
      const movieStartIndex = timeSlots.findIndex(ts => ts.id === movie.timeSlot.id);
      const slotIndex = timeSlots.findIndex(ts => ts.id === timeSlot.id);
      
      if (slotIndex >= movieStartIndex && slotIndex < movieStartIndex + movie.slotsSpanned) {
        return movie;
      }
    }
    return null;
  };

  const isArchivedSlotOccupiedSync = (date, timeSlot) => {
    return getArchivedMovieForSlot(date, timeSlot) !== null;
  };

  // Get available years for archive filtering
  // availableYears is now loaded via useEffect hook

  // Show loading state if calendar data is being loaded
  if (calendarDataLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 space-y-4">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-400"></div>
        <h3 className="text-xl font-semibold text-gray-400">Loading Calendar Data...</h3>
        <p className="text-gray-500">Fetching schedules and movie information...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Mode Selection */}
      <div className="flex justify-center space-x-2 mb-6">
        <button
          onClick={() => {
            setViewerMode('calendar');
            setShowPastCalendars(false);
          }}
          className={`px-6 py-3 rounded-md font-bold transition-colors ${
            viewerMode === 'calendar' && !showPastCalendars
              ? 'bg-orange-600 text-white'
              : 'text-orange-300 hover:text-orange-100 hover:bg-gray-700'
          }`}
        >
          📅 Current Schedule
        </button>
        <button
          onClick={() => {
            setViewerMode('nowPlaying');
            setShowPastCalendars(false);
          }}
          className={`px-6 py-3 rounded-md font-bold transition-colors ${
            viewerMode === 'nowPlaying' && !showPastCalendars
              ? 'bg-orange-600 text-white'
              : 'text-orange-300 hover:text-orange-100 hover:bg-gray-700'
          }`}
        >
          🎬 Now Playing
        </button>

        <button
          onClick={() => {
            setShowPastCalendars(true);
            if (!selectedArchiveYear && availableYears.length > 0) {
              setSelectedArchiveYear(availableYears[0].toString());
            }
          }}
          className={`px-6 py-3 rounded-md font-bold transition-colors flex items-center gap-2 ${
            showPastCalendars
              ? 'bg-purple-600 text-white'
              : 'text-orange-300 hover:text-orange-100 hover:bg-gray-700'
          }`}
        >
          <img 
            src="/Sarcophagus.webp" 
            alt="Sarcophagus" 
            className="w-5 h-5 object-contain"
          />
          Past Calendars
        </button>
      </div>


      {/* Timezone Selector - Only show for calendar and now playing modes */}
      {(viewerMode === 'calendar' || viewerMode === 'nowPlaying') && !showPastCalendars && (
        <div className="flex justify-center mb-6">
          <div className="bg-gray-800 border-2 border-orange-600 rounded-lg p-4 shadow-lg">
            <label className="block text-orange-300 text-sm font-bold mb-2">Your Timezone:</label>
            <select
              value={viewerTimezone}
              onChange={(e) => setViewerTimezone(e.target.value)}
              className="bg-gray-700 border border-orange-500 rounded px-3 py-2 text-white"
            >
              {timezones.map(tz => (
                <option key={tz.value} value={tz.value}>{tz.label}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Schedule Title */}
      {viewerMode === 'calendar' && !showPastCalendars && (
        <div className="text-center mb-6">
          <h2 className="text-3xl font-bold text-orange-300 mb-2">⚰️ Funeral arrangements</h2>
          <p className="text-orange-200">Your guide to the upcoming horror movie screenings</p>
        </div>
      )}

      {/* Current Calendar View */}
      {viewerMode === 'calendar' && !showPastCalendars && (
        <div className="grid gap-6">
          {/* An empty calendar means one of two very different things. Signed
              out it is simply not fetched, so say so plainly rather than
              reporting an error for something that is working as designed.
              Signed in it is a genuine anomaly, and keeps its diagnostic. */}
          {calendarWeeks.length === 0 && !isUserAuthenticated && (
            <div className="bg-gray-800 border-2 border-orange-600 rounded-lg p-6 text-center">
              <h3 className="text-xl font-bold text-orange-300 mb-2">🔒 Please log in to see the calendar</h3>
              <p className="text-orange-200">
                The screening schedule is only visible to festival members. Use
                Sign In above and we will email you a code.
              </p>
            </div>
          )}

          {calendarWeeks.length === 0 && isUserAuthenticated && (
            <div className="bg-red-800 border-2 border-red-600 rounded-lg p-4 text-center">
              <p className="text-red-200">⚠️ No calendar weeks available. Current timezone: {viewerTimezone}</p>
            </div>
          )}
          
          {/* Desktop View - Hidden on mobile */}
          <div className="hidden lg:block">
            {calendarWeeks.map((week, weekIndex) => (
              <div key={weekIndex} className="bg-gray-800 border-2 border-orange-600 rounded-lg shadow-2xl overflow-hidden">
                <div className="bg-gradient-to-r from-purple-900 to-orange-900 px-4 py-3 border-b-2 border-orange-500">
                  <h3 className="text-xl font-bold text-orange-300">
                    {getWeekRange(week)} ({viewerTimezone})
                  </h3>
                </div>
                <div className="overflow-x-auto">
                  <div className="min-w-full">
                    {/* Time slots header */}
                    <div className="grid grid-cols-8 bg-gray-700 border-b border-orange-500">
                      <div className="p-2 text-sm font-bold text-orange-300 border-r border-orange-600">Time</div>
                      {week.map(date => (
                        <div key={date.toISOString()} className="p-2 text-sm font-bold text-orange-300 text-center border-r border-orange-600 last:border-r-0">
                          <div>{date.toLocaleDateString('en-US', { weekday: 'short' })}</div>
                          <div>{date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>
                        </div>
                      ))}
                    </div>
                    
                    {/* Time slots */}
                    {timeSlots.map(timeSlot => (
                      <div key={timeSlot.id} className="grid grid-cols-8 border-b border-gray-600">
                        <div className="p-2 text-sm font-semibold text-orange-200 bg-gray-700 border-r border-orange-600">
                          {convertTimeToViewerTimezone(timeSlot.time)}
                        </div>
                        {week.map(date => {
                          const dateKey = date.toISOString().split('T')[0];
                          const scheduledMovie = getScheduledMovieForSlot(date, timeSlot);
                          const isOccupied = isSlotOccupied(date, timeSlot);
                          const isFirstSlotOfMovie = scheduledMovie && scheduledMovie.timeSlot.id === timeSlot.id;
                          
                          return (
                            <div 
                              key={`${dateKey}-${timeSlot.id}`} 
                              className={`border-r border-gray-600 last:border-r-0 min-h-[60px] relative ${
                                isOccupied ? 'bg-purple-800' : 'bg-gray-800'
                              }`}
                            >
                              {isFirstSlotOfMovie && scheduledMovie && (
                                <div 
                                  className={`absolute inset-0 m-1 rounded cursor-pointer transition-all hover:scale-105 hover:shadow-lg ${scheduledMovie.movie.color || 'bg-orange-600'} text-white text-xs flex flex-col justify-center items-center text-center p-1`}
                                  style={{ 
                                    height: `${scheduledMovie.slotsSpanned * 60 - 8}px`,
                                    zIndex: 10 
                                  }}
                                  onClick={() => onMovieClick && onMovieClick(scheduledMovie)}
                                >
                                  <div className="font-bold leading-tight mb-1">{scheduledMovie.movie.title}</div>
                                  <div className="text-xs opacity-75">{scheduledMovie.movie.year}</div>
                                  {scheduledMovie.movie.preferredStreamingService && (() => {
                                    const service = getStreamingServiceById(scheduledMovie.movie.preferredStreamingService);
                                    return service ? (
                                      <div className="mt-1">
                                        <StreamingServiceIcon 
                                          service={service}
                                          size="xs"
                                          showName={false}
                                        />
                                      </div>
                                    ) : null;
                                  })()}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Mobile View - Single day with navigation */}
          <div className="lg:hidden">
            {/* Signed-out people already have the notice above, which is not
                inside either breakpoint wrapper - without this condition they
                got two stacked banners on a phone. */}
            {calendarWeeks.length === 0 && isUserAuthenticated && (
              <div className="bg-red-800 border-2 border-red-600 rounded-lg p-4 text-center">
                <p className="text-red-200">⚠️ No calendar weeks available for mobile view. Current timezone: {viewerTimezone}</p>
              </div>
            )}
            
            {calendarWeeks.length > 0 && getCurrentDay() && (
              <div className="bg-gray-800 border-2 border-orange-600 rounded-lg shadow-2xl overflow-hidden">
                {/* Mobile Header with Navigation */}
                <div className="bg-gradient-to-r from-purple-900 to-orange-900 px-4 py-3 border-b-2 border-orange-500">
                  <div className="flex items-center justify-between">
                    <button
                      onClick={() => navigateDay('prev')}
                      disabled={!canNavigatePrev()}
                      className={`p-2 rounded ${
                        canNavigatePrev() 
                          ? 'text-orange-300 hover:bg-orange-600/20' 
                          : 'text-gray-500 cursor-not-allowed'
                      }`}
                    >
                      <ChevronLeft size={20} />
                    </button>
                    
                    <div className="text-center">
                      <h3 className="text-lg font-bold text-orange-300">
                        {getCurrentDay().toLocaleDateString('en-US', { 
                          weekday: 'long',
                          month: 'long', 
                          day: 'numeric' 
                        })}
                      </h3>
                      <div className="text-sm text-orange-200">
                        ({viewerTimezone}) • Day {selectedDayIndex + 1} of {getCurrentWeek().length}
                      </div>
                    </div>
                    
                    <button
                      onClick={() => navigateDay('next')}
                      disabled={!canNavigateNext()}
                      className={`p-2 rounded ${
                        canNavigateNext() 
                          ? 'text-orange-300 hover:bg-orange-600/20' 
                          : 'text-gray-500 cursor-not-allowed'
                      }`}
                    >
                      <ChevronRight size={20} />
                    </button>
                  </div>
                </div>

                {/* Mobile Day Schedule */}
                <div className="divide-y divide-gray-600">
                  {timeSlots.map(timeSlot => {
                    const currentDay = getCurrentDay();
                    const scheduledMovie = getScheduledMovieForSlot(currentDay, timeSlot);
                    const isOccupied = isSlotOccupied(currentDay, timeSlot);
                    const isFirstSlotOfMovie = scheduledMovie && scheduledMovie.timeSlot.id === timeSlot.id;
                    
                    return (
                      <div 
                        key={timeSlot.id} 
                        className={`flex min-h-[80px] ${
                          isOccupied ? 'bg-purple-800/30' : 'bg-gray-800'
                        }`}
                      >
                        {/* Time Column */}
                        <div className="w-20 flex-shrink-0 p-3 bg-gray-700 border-r border-orange-600 flex items-center justify-center">
                          <div className="text-sm font-semibold text-orange-200 text-center">
                            {convertTimeToViewerTimezone(timeSlot.time)}
                          </div>
                        </div>
                        
                        {/* Content Column */}
                        <div className="flex-1 p-3 relative">
                          {isFirstSlotOfMovie && scheduledMovie ? (
                            <div 
                              className={`h-full rounded cursor-pointer transition-all hover:scale-[1.02] hover:shadow-lg ${scheduledMovie.movie.color || 'bg-orange-600'} text-white p-3 flex flex-col justify-center`}
                              onClick={() => onMovieClick && onMovieClick(scheduledMovie)}
                            >
                              <div className="font-bold text-base mb-1">{scheduledMovie.movie.title}</div>
                              <div className="text-sm opacity-75 mb-2">{scheduledMovie.movie.year}</div>
                              {scheduledMovie.movie.preferredStreamingService && (() => {
                                const service = getStreamingServiceById(scheduledMovie.movie.preferredStreamingService);
                                return service ? (
                                  <div className="flex items-center gap-2">
                                    <StreamingServiceIcon 
                                      service={service}
                                      size="sm"
                                      showName={true}
                                    />
                                  </div>
                                ) : null;
                              })()}
                            </div>
                          ) : isOccupied ? (
                            <div className="h-full flex items-center justify-center text-gray-400 text-sm">
                              {/* Movie continues from previous slot */}
                            </div>
                          ) : (
                            <div className="h-full flex items-center justify-center text-gray-500 text-sm">
                              No movie scheduled
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Now Playing View */}
      {viewerMode === 'nowPlaying' && !showPastCalendars && (
        <NowPlaying 
          currentMovie={currentMovie}
          movieIndex={validIndex}
          totalMovies={upcomingMovies.length}
          onPrevious={() => setCurrentMovieIndex(Math.max(0, validIndex - 1))}
          onNext={() => setCurrentMovieIndex(Math.min(upcomingMovies.length - 1, validIndex + 1))}
          onOpenModal={(movie) => onMovieClick && onMovieClick(movie)}
          timezone={viewerTimezone}
          convertTimeToViewerTimezone={convertTimeToViewerTimezone}
          getStreamingServiceById={getStreamingServiceById}
        />
      )}

      {/* Past Calendars View */}
      {showPastCalendars && (
        <div>
          {/* Year Selector */}
          <div className="flex justify-center mb-6">
            <div className="bg-gray-800 border-2 border-orange-600 rounded-lg p-4 shadow-lg">
              <label className="block text-orange-300 text-sm font-bold mb-2">Select Year:</label>
              <select
                value={selectedArchiveYear}
                onChange={(e) => setSelectedArchiveYear(e.target.value)}
                className="bg-gray-700 border border-orange-500 rounded px-3 py-2 text-white"
              >
                <option value="">Choose a year...</option>
                {availableYears.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Archive Calendar */}
          {selectedArchiveYear && (
            <ArchiveCalendar
              selectedYear={selectedArchiveYear}
              archivedWeeks={archivedWeeksForYear}
              getArchivedWeeksForYear={() => archivedWeeksForYear}
              getAvailableYears={() => availableYears}
              onYearChange={setSelectedArchiveYear}
              getScheduledMovieForSlot={getArchivedMovieForSlot}
              isSlotOccupied={isArchivedSlotOccupiedSync}
              timeSlots={timeSlots}
              timezone={timezone}
              viewerTimezone={viewerTimezone}
              convertTimeToViewerTimezone={convertTimeToViewerTimezone}
              onMovieClick={onMovieClick}
              isAdmin={isAdmin}
              onUnarchiveWeek={onUnarchiveWeek}
              getStreamingServiceById={getStreamingServiceById}
            />
          )}
        </div>
      )}

      {/* Reviews and Hall of Horrors moved to dedicated Reviews tab */}

    </div>
  );
};

export default ViewerTab;
